import React, { useState, useMemo, useRef, useEffect } from "react";
import CalendarDropdown from "./CalendarDropdown";
import {
  getMinDateForPassengerType,
  getMaxDateForPassengerType,
  toDDMMYYYY,
} from "../../utils/dateUtils";
import { FaCalendarAlt } from "react-icons/fa";

interface DatePickerWithValidationProps {
  selected?: string;
  onSelect?: (date: string) => void;
  passengerType?: "ADT" | "CHD" | "INF";
  showAgeValidation?: boolean;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  disabled?: boolean;
}

const DatePickerWithValidation: React.FC<DatePickerWithValidationProps> = ({
  selected,
  onSelect,
  passengerType,
  showAgeValidation = false,
  minDate,
  maxDate,
  placeholder = "Select date",
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  const computedMinDate = useMemo(() => {
    if (passengerType && showAgeValidation) {
      return getMinDateForPassengerType(passengerType);
    }
    return minDate;
  }, [passengerType, showAgeValidation, minDate]);

  const computedMaxDate = useMemo(() => {
    if (passengerType && showAgeValidation) {
      return getMaxDateForPassengerType(passengerType);
    }
    return maxDate;
  }, [passengerType, showAgeValidation, maxDate]);

  useEffect(() => {
    if (selected) {
      setManualInput(toDDMMYYYY(selected));
    } else {
      setManualInput("");
    }
  }, [selected]);

  const handleSelect = (date: string) => {
    if (onSelect) onSelect(date);
    setIsOpen(false);
  };

  const handleManualInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setManualInput(value);

    // Clearing the field clears the value, so an optional date (like an end
    // date) can be removed rather than left stranded at its last value.
    if (value.trim() === "") {
      if (onSelect) onSelect("");
      return;
    }

    const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = value.match(dateRegex);

    if (match) {
      const [, day, month, year] = match;
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

      if (
        date.getDate() === parseInt(day) &&
        date.getMonth() === parseInt(month) - 1 &&
        date.getFullYear() === parseInt(year)
      ) {
        const formatted = [
          date.getFullYear(),
          String(date.getMonth() + 1).padStart(2, "0"),
          String(date.getDate()).padStart(2, "0"),
        ].join("-");

        if (onSelect) onSelect(formatted);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex">
        <input
          type="text"
          placeholder={placeholder}
          value={manualInput}
          onChange={handleManualInputChange}
          disabled={disabled}
          className="block w-full px-3 py-2 rounded-l-xl border border-gray-300 shadow-sm bg-white"
        />
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className="px-3 py-2 rounded-r-xl border border-l-0 border-gray-300 shadow-sm bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
        >
          <FaCalendarAlt className="text-primary w-5 h-5 align-middle" />
        </button>
      </div>

      {isOpen && !disabled && (
        <CalendarDropdown
          selected={selected}
          onSelect={handleSelect}
          minDate={computedMinDate}
          maxDate={computedMaxDate}
          maxYear={
            computedMaxDate
              ? new Date(computedMaxDate).getFullYear()
              : undefined
          }
        />
      )}
    </div>
  );
};

export default DatePickerWithValidation;
